import CBRELogo from "@/assets/images/cbreLogo.svg?react";
import type { Survey, SurveyGroup } from "@/features/survey/types";
import MarketsDropdown from "@/pages/components/MarketsDropdown";
import PdfDrawer from "@/shared/components/PdfDrawer";
import {
  getShortlistedProperties,
  isShortlistEnabled,
} from "@/shared/utils/propertyUtils";
import { EmeraldIcon } from "@emerald-react/icon";
import { useState } from "react";
import { Link, useParams } from "react-router";

interface GroupTabStripProps {
  surveyGroup: SurveyGroup;
  surveys: Survey[];
  activeTab?: "market" | "shortlist";
}

/**
 * Persistent survey-group tab strip shown above a member survey's own menu
 * bar, so Overview/other markets stay reachable while browsing one market.
 */
const GroupTabStrip = ({
  surveyGroup,
  surveys,
  activeTab = "market",
}: GroupTabStripProps) => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [isShortlistedOnly, setIsShortlistedOnly] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const showShortlist = isShortlistEnabled(null, surveyGroup);
  const shortlistCount = surveys.reduce(
    (total, survey) => total + getShortlistedProperties(survey).length,
    0,
  );
  const isShortlistTab = activeTab === "shortlist";
  const shortlistPath = `/interactive/group/${surveyGroup.surveyGroupId}/shortlist`;
  // PDF export depends on a loaded market survey, which the shortlist page doesn't have
  const showPdfDownload =
    !isShortlistTab &&
    surveyGroup.clientInteractionControls?.isClientPdfDownloadEnabled;

  return (
    <>
      <header className="flex justify-between items-center p-6 lg:px-8 h-18 bg-primary-dark shadow-header-elevation">
        <div className="flex flex-col lg:flex-row items-center">
          <Link to={`/interactive/group/${surveyGroup.surveyGroupId}/cover`}>
            <CBRELogo className="w-24 h-6.5" />
          </Link>
          <div className="header-divider"></div>

          <div className="hidden lg:block">
            <div className="flex items-baseline nav-items">
              <Link
                to={`/interactive/group/${surveyGroup.surveyGroupId}/cover`}
                className="nav-item"
              >
                Overview
              </Link>
              <MarketsDropdown
                surveys={surveys}
                activeSurveyId={surveyId}
                isActive={!isShortlistTab}
              />
              {showShortlist && (
                <Link
                  to={shortlistPath}
                  className={`nav-item ${isShortlistTab ? "active" : ""}`}
                >
                  Shortlist
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#17e88f] text-xs font-bold text-[#003f2d]">
                    {shortlistCount}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="lg:flex items-stretch hidden">
          {showPdfDownload && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsPdfDrawerOpen(true);
              }}
              className="nav-item"
            >
              <EmeraldIcon icon="download" type="outlined" variant="symbol" />
              <span>PDF</span>
            </a>
          )}
          <a
            href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
          >
            Disclaimer
          </a>
        </div>

        <div className="lg:hidden flex gap-x-2 items-center">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-200 hover:text-green-300 focus:outline-none"
          >
            <span className="sr-only">
              {isMobileMenuOpen ? "Close group menu" : "Open group menu"}
            </span>
            <EmeraldIcon icon={isMobileMenuOpen ? "close" : "menu"} />
          </button>
        </div>
      </header>

      {/* Mobile group menu — same reachable items as the desktop nav-items/right-side links above */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-150 top-18">
          <div className="px-2 pt-2 pb-3 space-y-1 w-screen h-screen overflow-auto sm:px-3 bg-primary-dark">
            <Link
              to={`/interactive/group/${surveyGroup.surveyGroupId}/cover`}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
            >
              Overview
            </Link>
            <p className="block px-3 py-2 text-sm font-semibold text-white">
              {`Markets (${surveys.length})`}
            </p>
            {surveys.map((survey) => (
              <Link
                key={survey.id}
                to={`/interactive/${survey.id}/marketmap`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-6 py-2 rounded-md text-sm font-normal transition-colors ${
                  survey.id === surveyId
                    ? "text-white underline font-semibold"
                    : "text-gray-200 hover:text-green-300"
                }`}
              >
                {survey.name}
              </Link>
            ))}

            {showShortlist && (
              <Link
                to={shortlistPath}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isShortlistTab
                    ? "text-white underline font-semibold"
                    : "font-normal text-gray-200 hover:text-green-300"
                }`}
              >
                Shortlist
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#17e88f] text-xs font-bold text-[#003f2d]">
                  {shortlistCount}
                </span>
              </Link>
            )}

            {showPdfDownload && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  setIsPdfDrawerOpen(true);
                }}
                className="px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors flex items-center space-x-2"
              >
                <EmeraldIcon icon="download" type="outlined" variant="symbol" />
                <span>PDF</span>
              </a>
            )}

            <a
              href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
            >
              Disclaimer
            </a>
          </div>
        </div>
      )}

      <PdfDrawer
        isOpen={isPdfDrawerOpen}
        isShortlistedOnly={isShortlistedOnly}
        onClose={() => setIsPdfDrawerOpen(false)}
        shortListToggleStatus={setIsShortlistedOnly}
      />
    </>
  );
};

export default GroupTabStrip;

